import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Sparkles, ArrowRight, ChevronLeft, ChevronRight, Star } from 'lucide-react';

const WHATSAPP = '918889616972';

const COLLECTION_ACCENTS = {
  'pet-anime':        { color: '#f472b6', bg: '#fdf2f8', label: '🐾 Pet Anime'    },
  'couple-anime':     { color: '#a78bfa', bg: '#f5f3ff', label: '💑 Couple'       },
  'corporate-custom': { color: '#38bdf8', bg: '#f0f9ff', label: '🏢 Corporate'    },
  'self-custom':      { color: '#fb923c', bg: '#fff7ed', label: '✨ Custom'        },
};

const MiniProductCard = ({ product }) => {
  const [hovered, setHovered] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const accent = COLLECTION_ACCENTS[product.collection] || COLLECTION_ACCENTS['self-custom'];
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  const handleOrder = (e) => {
    e.preventDefault();
    const msg = `Hi AuraPrintiqo! 👋\n\nI want to order:\n*${product.name}*\nPrice: ₹${product.price}\n\nPlease guide me!`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Link
      to={`/shop/${product.id}`}
      className="block flex-shrink-0 w-52 sm:w-60 snap-start"
      onMouseEnter={() => { setHovered(true); if (product.images?.[1]) setTimeout(() => setImgIdx(1), 150); }}
      onMouseLeave={() => { setHovered(false); setImgIdx(0); }}
    >
      <div
        className="rounded-3xl overflow-hidden transition-all duration-400"
        style={{
          boxShadow: hovered
            ? `0 20px 48px -8px ${accent.color}45, 0 6px 20px rgba(0,0,0,0.1)`
            : '0 4px 16px rgba(0,0,0,0.06)',
          transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        }}
      >
        {/* Image */}
        <div
          className="relative overflow-hidden"
          style={{ aspectRatio: '3/4', background: accent.bg }}
        >
          <img
            src={product.images?.[imgIdx] || product.images?.[0]}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-all duration-600"
            style={{ transform: hovered ? 'scale(1.07)' : 'scale(1)' }}
          />

          {/* Gradient */}
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{
              background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.65) 100%)',
              opacity: hovered ? 1 : 0.75,
            }}
          />

          {/* Top badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.badge && (
              <span
                className="px-2 py-0.5 text-[10px] font-bold rounded-lg text-white shadow"
                style={{ background: accent.color }}
              >
                {product.badge}
              </span>
            )}
            {discount && (
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-lg bg-emerald-500 text-white shadow">
                -{discount}%
              </span>
            )}
          </div>

          {/* Custom badge */}
          {product.customisable && (
            <div className="absolute top-3 right-3">
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold"
                style={{ background: 'rgba(255,255,255,0.92)', color: accent.color }}
              >
                <Sparkles className="w-2.5 h-2.5" /> Custom
              </span>
            </div>
          )}

          {/* Bottom info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <p
              className="text-[10px] font-bold tracking-wider uppercase mb-0.5"
              style={{ color: accent.color }}
            >
              {accent.label}
            </p>
            <h3 className="text-white font-bold text-sm leading-snug line-clamp-1 mb-1">
              {product.name}
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-1.5">
                <span className="text-white font-bold text-sm">₹{product.price.toLocaleString('en-IN')}</span>
                {product.originalPrice && (
                  <span className="text-white/50 text-xs line-through">₹{product.originalPrice.toLocaleString('en-IN')}</span>
                )}
              </div>
              <div className="flex items-center gap-0.5">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                <span className="text-white/70 text-[10px]">5.0</span>
              </div>
            </div>
          </div>

          {/* WhatsApp CTA — slides up on hover */}
          <div
            className="absolute left-3 right-3 transition-all duration-300"
            style={{
              bottom: hovered ? '72px' : '60px',
              opacity: hovered ? 1 : 0,
              transform: hovered ? 'translateY(0)' : 'translateY(6px)',
            }}
          >
            <button
              onClick={handleOrder}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-white text-xs font-bold shadow-lg"
              style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)' }}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Order Now
            </button>
          </div>
        </div>

        {/* Color swatches strip */}
        <div
          className="flex items-center gap-1.5 px-3 py-2 bg-white transition-colors duration-300"
          style={{ background: hovered ? accent.bg : 'white' }}
        >
          {(product.colors || []).slice(0, 5).map(c => (
            <div
              key={c.name}
              title={c.name}
              className="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0"
              style={{ backgroundColor: c.hex }}
            />
          ))}
          {(product.colors || []).length > 5 && (
            <span className="text-[10px] text-gray-400">+{product.colors.length - 5}</span>
          )}
          <div className="ml-auto flex gap-0.5">
            {(product.sizes || []).slice(0, 3).map(s => (
              <span key={s} className="text-[9px] px-1 py-0.5 bg-gray-100 rounded text-gray-500">{s}</span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
};

/**
 * HomepageProductSlide
 * Drop this into HomePage below the hero section.
 * Fetches products from localStorage (same source as /shop).
 */
const HomepageProductSlide = ({ products = [] }) => {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 10);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  if (!products.length) return null;

  return (
    <section className="py-14 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[hsl(350,70%,55%)] mb-2">
              <Sparkles className="w-3.5 h-3.5" /> Featured Products
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Designs You'll <span className="bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] bg-clip-text text-transparent">Love</span>
            </h2>
            <p className="text-gray-500 text-sm mt-1">Ready-made or fully customisable with your photo</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Arrow buttons */}
            <button
              onClick={() => scroll(-1)}
              disabled={!canLeft}
              className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-500 hover:border-[hsl(350,70%,65%)] hover:text-[hsl(350,70%,55%)] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll(1)}
              disabled={!canRight}
              className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-500 hover:border-[hsl(350,70%,65%)] hover:text-[hsl(350,70%,55%)] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* View all — desktop only */}
            <Link
              to="/shop"
              className="hidden sm:flex items-center gap-1.5 ml-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow hover:shadow-lg transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}
            >
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Scroll strip */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
        >
          {products.map((product) => (
            <MiniProductCard key={product.id} product={product} />
          ))}

          {/* End card — view all */}
          <Link
            to="/shop"
            className="flex-shrink-0 w-44 snap-start flex items-center justify-center"
          >
            <div
              className="flex flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed p-8 h-full min-h-[260px] transition-all hover:scale-105 cursor-pointer"
              style={{ borderColor: 'hsl(350,70%,65%,0.35)', color: 'hsl(350,70%,55%)' }}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ background: 'hsl(350,70%,65%,0.1)' }}
              >
                <ArrowRight className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold text-center leading-snug">
                See all<br />products
              </p>
            </div>
          </Link>
        </div>

        {/* View all — mobile */}
        <div className="sm:hidden text-center mt-5">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold text-white shadow hover:shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}
          >
            View All Products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomepageProductSlide;
